import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import FilterDrawer from '../components/FilterDrawer';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Search, Download, Upload, Plus, Edit, Trash2, Eye, Clock,
  Users, UserCheck, UserPlus, UserX, AlertCircle, Ban, CheckCircle,
  Package, Truck, RotateCcw, XCircle, DollarSign, SlidersHorizontal, CreditCard,
  Mail, Phone, Calendar as CalendarIcon, TrendingUp, Loader2, FileText, Printer, MoreVertical,
  CheckSquare, XSquare, Crown, CalendarClock, MessageSquare, ChevronRight, ChevronDown, ExternalLink, Sparkles, AlertTriangle
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { useToast } from '../hooks/use-toast';
import { toast as sonnerToast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { OrdersListLite } from '../components/lite';
import { BookingsListLite } from '../components/lite';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
// iter-235 SSOT — shipment CTA matrix + action labels
import { ACTION_LABELS } from '../lib/shipmentCtaMatrix';
import { useShipmentState } from '../hooks/useShipmentState';
import { downloadAuthedFile } from '../utils/downloadAuthedFile';

// ============================================================================
// SHIPMENT ROW ACTIONS — SSOT-driven overflow menu items
// ============================================================================
// Reads the canonical shipment_state stamped by the backend resolver
// and renders one DropdownMenuItem per CTA in the matrix. This makes
// the Orders List row overflow menu render the EXACT same actions as
// the Order Details page for any given state — overflow consistency
// guarded by `test_order_list_overflow_matches_cta_matrix()`.
const ShipmentRowActions = ({ order, navigate, toast }) => {
  const { ctas } = useShipmentState(order);
  if (!ctas || ctas.length === 0) return null;

  // Map each canonical action key → handler + icon. Handlers that
  // require rich UI (modal, scheduler) navigate to OrderDetailsNew
  // where the controls already live — keeps the overflow lightweight
  // while still exposing the action exactly as the matrix requires.
  const handlers = {
    ship_with_delhivery: () => navigate(`/orders/${order.id}#ship`),
    print_label: async () => {
      const url = order.label_url
        || order.shipment?.label_url
        || `/api/delhivery/shipments/${order.id}/label`;
      const filename = `${order.order_number || order.id}_label.pdf`;
      const res = await downloadAuthedFile(url, filename);
      if (!res.ok) {
        toast({
          title: 'Could not download label',
          description: res.error || 'Try again from Order Details.',
          variant: 'destructive',
        });
      }
    },
    schedule_pickup: () => navigate(`/orders/${order.id}#schedule`),
    cancel_awb: () => navigate(`/orders/${order.id}#cancel-awb`),
    track_shipment: () => {
      const url = order.tracking_url || order.shipment?.tracking_url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        navigate(`/orders/${order.id}#track`);
      }
    },
    view_shipment_history: () => navigate(`/orders/${order.id}#history`),
  };

  const icons = {
    ship_with_delhivery:   Truck,
    print_label:           Printer,
    schedule_pickup:       CalendarIcon,
    cancel_awb:            XCircle,
    track_shipment:        ExternalLink,
    view_shipment_history: Eye,
  };

  return (
    <>
      <DropdownMenuSeparator />
      {ctas.map((action) => {
        const Icon = icons[action];
        const handler = handlers[action];
        if (!handler) return null;   // never invent an unknown action
        const danger = action === 'cancel_awb';
        return (
          <DropdownMenuItem
            key={action}
            data-testid={`order-row-cta-${action}-${order.id}`}
            onClick={(e) => {
              e.stopPropagation();
              handler();
            }}
            className={danger ? 'text-red-600' : ''}
          >
            {Icon && <Icon className="h-4 w-4 mr-2" />}
            {ACTION_LABELS[action] || action}
          </DropdownMenuItem>
        );
      })}
    </>
  );
};

// ============================================================================
// RESOURCE SCHEMAS
// ============================================================================
const RESOURCE_SCHEMAS = {
  vendors: {
    id: 'vendors',
    title: 'Vendors',
    subtitle: 'Manage vendor accounts and performance',
    apiEndpoint: '/api/vendors/',
    detailRoute: '/vendors',
    createRoute: '/vendors/new',
    exportEndpoint: '/api/vendors/export/csv',
    importEndpoint: '/api/vendors/import/csv',
    templateEndpoint: '/api/vendors/template/csv',
    hasExport: true,
    exportSuperAdminOnly: true,
    hasImport: true,
    hasCreate: true,
    hasStats: false,
    hasTabs: true,
    hasFilterDrawer: true,
    tabs: [
      { id: 'active_compliant', label: 'Active & Compliant', icon: CheckCircle },
      { id: 'pending', label: 'Pending', icon: Clock },
      { id: 'needs_review', label: 'Needs Review', icon: AlertCircle },
      { id: 'suspended', label: 'Suspended', icon: Ban },
      { id: 'all', label: 'All', icon: Users }
    ],
    columns: [
      { key: 'name', label: 'Vendor', render: 'vendor_info' },
      { key: 'business', label: 'Business', render: 'text', accessor: 'vendor_profile.business_details.business_name' },
      { key: 'status', label: 'Status', render: 'status_badge' },
      { key: 'kyc', label: 'KYC', render: 'kyc_badge' },
      { key: 'sales', label: 'Sales Volume', render: 'currency', accessor: 'vendor_profile.metrics.total_sales' },
      { key: 'products', label: 'Products', render: 'number', accessor: 'vendor_profile.metrics.products_listed' },
      { key: 'dispute', label: 'Dispute %', render: 'dispute_badge', accessor: 'vendor_profile.metrics.dispute_rate' },
      { key: 'commission', label: 'Commission', render: 'commission', accessor: 'vendor_profile.commission.rate' },
      { key: 'subscription_plan_name', label: 'Plan', render: 'subscription_plan', afterActions: true },
      { key: 'trial_end', label: 'Trial End', render: 'trial_end_date', afterActions: true }
    ],
    sortOptions: [
      { value: 'sales_desc', label: 'Highest Sales' },
      { value: 'dispute_asc', label: 'Best Performance' },
      { value: 'last_activity_desc', label: 'Last Activity' },
      { value: 'name', label: 'Name' },
      { value: 'joining_date_desc', label: 'Newest Joined' },
      { value: 'joining_date_asc', label: 'Oldest Joined' },
      { value: 'listings_desc', label: 'Most Listings' },
      { value: 'listings_asc', label: 'Fewest Listings' }
    ],
    extraFilters: ['vendorStatus', 'vendorKyc', 'vendorLocation', 'vendorCategory', 'vendorPlan'],
    actions: ['view', 'view_billing', 'view_listings', 'send_message', 'kyc_approve', 'activate_deactivate', 'commission']
  },
  
  customers: {
    id: 'customers',
    title: 'Customers',
    subtitle: 'Manage customer data and engagement',
    apiEndpoint: '/api/customers/',
    detailRoute: '/customers',
    createRoute: null,
    exportEndpoint: '/api/customers/export/csv',
    importEndpoint: '/api/customers/import/csv',
    hasExport: true,
    hasImport: true,
    hasCreate: true,
    hasStats: false,
    hasTabs: true,
    hasFilterDrawer: true,
    tabs: [
      { id: 'active', label: 'Active', icon: UserCheck },
      { id: 'new_signups', label: 'New', icon: Users },
      { id: 'at_risk', label: 'At Risk', icon: AlertCircle },
      { id: 'suspended', label: 'Suspended', icon: Ban },
      { id: 'all', label: 'All', icon: Users }
    ],
    columns: [
      { key: 'name', label: 'Name', render: 'text' },
      { key: 'email', label: 'Email', render: 'text' },
      { key: 'status', label: 'Status', render: 'status_badge' },
      { key: 'lifetime_value', label: 'Lifetime Value', render: 'currency' },
      { key: 'last_activity', label: 'Last Activity', render: 'date_with_ago' },
      { key: 'created_at', label: 'Signup Date', render: 'date' }
    ],
    sortOptions: [
      { value: 'last_activity_desc', label: 'Last Activity' },
      { value: 'ltv_desc', label: 'Highest LTV' },
      { value: 'signup_date_desc', label: 'Newest First' },
      { value: 'name', label: 'Name' }
    ],
    actions: ['view', 'edit', 'delete'],
    dialogs: ['add', 'edit', 'delete']
  },
  
  orders: {
    id: 'orders',
    title: 'Orders',
    subtitle: 'Manage all orders from products, services, and rentals',
    apiEndpoint: '/api/orders/',
    detailRoute: '/orders',
    createRoute: '/orders/new',
    exportEndpoint: '/api/orders/export/csv',
    hasExport: true,
    hasImport: false,
    hasCreate: true,
    hasStats: false,
    hasTabs: true,
    hasFilterDrawer: false,
    hasMobileCards: true,
    tabs: [
      { id: 'all', label: 'All', icon: Package },
      { id: 'action_required', label: 'Action', icon: AlertCircle },
      { id: 'ready_to_ship', label: 'Ready', icon: Package },
      { id: 'in_transit', label: 'Transit', icon: Truck },
      { id: 'delivered', label: 'Delivered', icon: CheckCircle },
      { id: 'returns', label: 'Returns', icon: RotateCcw },
      { id: 'failed', label: 'Failed', icon: XCircle }
    ],
    columns: [
      { key: 'order_number', label: 'Order #', render: 'order_id' },
      { key: 'customer', label: 'Customer', render: 'customer_info' },
      { key: 'vendor_name', label: 'Vendor', render: 'text', adminOnly: true },
      { key: 'items', label: 'Items', render: 'item_count' },
      { key: 'order_type', label: 'Type', render: 'type_badge' },
      { key: 'total_amount', label: 'Amount', render: 'currency' },
      { key: 'status', label: 'Status', render: 'order_status_badge' },
      { key: 'awb_number', label: 'AWB / Label', render: 'awb_info' },
      { key: 'created_at', label: 'Date', render: 'date_with_updated' }
    ],
    sortOptions: [
      { value: 'date_desc', label: 'Newest' },
      { value: 'date_asc', label: 'Oldest' },
      { value: 'customer', label: 'Customer' },
      { value: 'total_desc', label: 'High Amount' },
      { value: 'total_asc', label: 'Low Amount' }
    ],
    extraFilters: ['dateRange', 'paymentStatus'],
    actions: ['view', 'invoice']
  },
  
  leads: {
    id: 'leads',
    title: 'Leads Management',
    subtitle: 'Track and manage customer inquiries',
    apiEndpoint: '/api/leads/',
    statsEndpoint: '/api/leads/stats',
    detailRoute: null,
    hasExport: false,
    hasImport: false,
    hasCreate: false,
    hasStats: true,
    hasTabs: false,
    hasFilterDrawer: false,
    statsConfig: [
      { key: 'total', label: 'Total Leads', icon: Users, color: 'blue' },
      { key: 'new', label: 'New', icon: UserPlus, color: 'blue' },
      { key: 'contacted', label: 'Contacted', icon: Mail, color: 'yellow' },
      { key: 'converted', label: 'Converted', icon: CheckCircle, color: 'green' },
      { key: 'conversion_rate', label: 'Conversion Rate', icon: TrendingUp, color: 'purple', suffix: '%', decimals: 1 }
    ],
    columns: [
      { key: 'customer', label: 'Customer', render: 'lead_customer' },
      { key: 'listing', label: 'Listing', render: 'lead_listing' },
      { key: 'type', label: 'Type', render: 'lead_type' },
      { key: 'status', label: 'Status', render: 'lead_status' },
      { key: 'priority', label: 'Priority', render: 'priority_badge' },
      { key: 'created_at', label: 'Created', render: 'lead_date' }
    ],
    sortOptions: [],
    extraFilters: ['leadStatus', 'leadType'],
    actions: ['view', 'update']
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================
const getNestedValue = (obj, path) => {
  if (!path) return obj;
  return path.split('.').reduce((acc, part) => acc?.[part], obj);
};

const formatCurrency = (amount) => `₹${(amount || 0).toLocaleString('en-IN')}`;

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric'
  });
};

const daysSince = (dateString) => {
  if (!dateString) return 999;
  return Math.floor((new Date() - new Date(dateString)) / (1000 * 60 * 60 * 24));
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const UniversalResourceTable = ({ resourceType = 'vendors' }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { apiCall, user, isAdmin } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  
  // Get schema for current resource
  const schema = RESOURCE_SCHEMAS[resourceType];
  
  // Core state
  const [data, setData] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || schema.tabs?.[0]?.id || 'all');
  const [sortBy, setSortBy] = useState(schema.sortOptions?.[0]?.value || '');
  
  // Filter state
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({ selectedFilters: {}, rangeValues: {} });
  
  // Extra filters (for orders/leads)
  const [dateRange, setDateRange] = useState('all');
  const [paymentStatus, setPaymentStatus] = useState('all');
  const [needsRefundOnly, setNeedsRefundOnly] = useState(false);
  const [leadStatus, setLeadStatus] = useState('all');
  const [leadType, setLeadType] = useState('all');
  
  // Vendor-specific filters
  const [vendorStatus, setVendorStatus] = useState('all');
  const [vendorKyc, setVendorKyc] = useState('all');
  const [vendorLocation, setVendorLocation] = useState('all');
  const [vendorCategory, setVendorCategory] = useState('all');
  const [vendorPlan, setVendorPlan] = useState('all');
  
  // Dialog state
  const [selectedItem, setSelectedItem] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Admin → Vendor direct message dialog state
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [messageTarget, setMessageTarget] = useState(null);
  const [messageForm, setMessageForm] = useState({ title: '', message: '', priority: 'high', send_push: true, send_email: false });
  const [sendingMessage, setSendingMessage] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [commissionDialogOpen, setCommissionDialogOpen] = useState(false);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({});
  const [newCommission, setNewCommission] = useState('');
  const [updateData, setUpdateData] = useState({ status: '', priority: '', notes: '' });
  
  // Invoice state (orders)
  const [generatingInvoice, setGeneratingInvoice] = useState(null);
  const [zohoConnected, setZohoConnected] = useState(false);
  
  // Bulk selection state
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [expandedParents, setExpandedParents] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // Trial extension state
  const [trialDialogOpen, setTrialDialogOpen] = useState(false);
  const [trialTargetVendor, setTrialTargetVendor] = useState(null);
  const [trialDate, setTrialDate] = useState(null);
  const [trialSaving, setTrialSaving] = useState(false);

  // Change Plan (bulk) state
  const PLAN_OPTIONS = [
    { code: 'launch',       label: 'Launch',       commission: 18 },
    { code: 'basic',        label: 'Basic',         commission: 15 },
    { code: 'professional', label: 'Professional', commission: 12 },
    { code: 'business',     label: 'Business',     commission: 10 },
    { code: 'enterprise',   label: 'Enterprise',   commission:  8 },
  ];
  const [planDialogOpen,    setPlanDialogOpen]    = useState(false);
  const [selectedPlanCode,  setSelectedPlanCode]  = useState('');
  const [planChangeReason,  setPlanChangeReason]  = useState('');
  const [planChangeSaving,  setPlanChangeSaving]  = useState(false);
  
  // Shopify order sync state
  const [syncingOrders, setSyncingOrders] = useState(false);
  
  // Mobile detection for Lite view
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // ============================================================================
  // DATA FETCHING
  // ============================================================================
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build query params for leads
      let endpoint = schema.apiEndpoint;
      if (resourceType === 'leads') {
        const params = new URLSearchParams();
        if (leadStatus !== 'all') params.append('status', leadStatus);
        if (leadType !== 'all') params.append('listing_type', leadType);
        endpoint = `${schema.apiEndpoint}?${params.toString()}`;
      }
      
      const result = await apiCall(endpoint);
      let items = Array.isArray(result) ? result : [];

      // For vendors, health flags are now embedded inline in the list
      // response (iter-perf-1). If they're already present, skip the
      // second /health-flags round-trip. Keep the fallback for safety.
      if (resourceType === 'vendors' && items.length > 0) {
        const alreadyHasHealth = items.some(v => v._health != null);
        if (!alreadyHasHealth) {
          try {
            const hf = await apiCall('/api/vendors/health-flags');
            const byId = {};
            (hf?.vendors || []).forEach(v => { byId[v.vendor_id] = v.health; });
            items = items.map(v => ({ ...v, _health: byId[v.id] || null }));
          } catch (_e) {
            // Health flags are non-critical — never break the list if they fail.
          }
        }
      }

      setData(items);
    } catch (error) {
      toast({ title: 'Error', description: `Failed to load ${schema.title.toLowerCase()}`, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [apiCall, schema, resourceType, leadStatus, leadType, toast]);
  
  const fetchStats = useCallback(async () => {
    if (!schema.hasStats || !schema.statsEndpoint) return;
    try {
      const result = await apiCall(schema.statsEndpoint);
      setStats(result);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [apiCall, schema]);
  
  const checkZohoConnection = useCallback(async () => {
    if (resourceType !== 'orders') return;
    try {
      const status = await apiCall('/api/auth/zoho/status');
      setZohoConnected(status?.is_connected || false);
    } catch { setZohoConnected(false); }
  }, [apiCall, resourceType]);
  
  // Sync orders from Shopify
  const handleSyncShopifyOrders = async () => {
    setSyncingOrders(true);
    try {
      const result = await apiCall('/api/shopify-sync/retroactive-order-sync?limit=50', { method: 'POST' });
      toast({
        title: 'Order Sync Complete',
        description: result.message || `Synced ${result.total_synced || 0} orders from Shopify`,
      });
      // Refresh the orders list
      fetchData();
    } catch (error) {
      toast({
        title: 'Sync Failed',
        description: error.message || 'Failed to sync orders from Shopify. Check your Shopify connection.',
        variant: 'destructive'
      });
    } finally {
      setSyncingOrders(false);
    }
  };
  
  useEffect(() => {
    fetchData();
    fetchStats();
    checkZohoConnection();
  }, [fetchData, fetchStats, checkZohoConnection]);
  
  // ============================================================================
  // TAB FILTERING LOGIC
  // ============================================================================
  const getItemTab = useCallback((item) => {
    if (resourceType === 'vendors') {
      const disputeRate = item.vendor_profile?.metrics?.dispute_rate || 0;
      const isApproved = item.status === 'active';
      const isPending = item.status === 'pending';
      const isSuspended = item.status === 'suspended' || item.status === 'terminated';
      const gstVerified = item.vendor_profile?.verifications?.gst_verified || false;
      
      if (isApproved && gstVerified && disputeRate < 5) return 'active_compliant';
      if (isPending || (isApproved && !gstVerified)) return 'pending';
      if (isApproved && disputeRate >= 5) return 'needs_review';
      if (isSuspended) return 'suspended';
      return 'all';
    }
    
    if (resourceType === 'customers') {
      const days = daysSince(item.last_activity);
      const daysSinceSignup = daysSince(item.created_at);
      
      if (item.status === 'active' && days <= 180) return 'active';
      if (item.status === 'active' && daysSinceSignup <= 30) return 'new_signups';
      if (item.status === 'active' && days > 90) return 'at_risk';
      if (item.status === 'suspended' || item.status === 'banned') return 'suspended';
      return 'all';
    }
    
    if (resourceType === 'orders') {
      const status = item.status?.toLowerCase();
      if (['new', 'payment_confirmed', 'failed_delivery'].includes(status)) return 'action_required';
      if (['processing'].includes(status)) return 'ready_to_ship';
      if (['shipped', 'out_for_delivery'].includes(status)) return 'in_transit';
      if (status === 'delivered') return 'delivered';
      if (['return_requested', 'cancelled', 'refunded'].includes(status)) return 'returns';
      if (['payment_failed', 'verification_required', 'on_hold'].includes(status)) return 'failed';
      return 'all';
    }
    
    return 'all';
  }, [resourceType]);
  
  // ============================================================================
  // FILTERING & SORTING
  // ============================================================================
  const getDisplayData = useMemo(() => {
    let filtered = [...data];
    
    // Tab filter
    if (schema.hasTabs && activeTab !== 'all') {
      filtered = filtered.filter(item => getItemTab(item) === activeTab);
    }
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item => {
        if (resourceType === 'vendors') {
          // Match against all the places a business name may live (matches the
          // backend list_vendors waterfall: bd.business_name → legal_name →
          // trade_name → brand_name → identity.store_name → identity.display_name).
          const bd = item.vendor_profile?.business_details || {};
          const ident = item.identity || {};
          const haystack = [
            item.name,
            item.email,
            bd.business_name,
            bd.legal_name,
            bd.trade_name,
            bd.brand_name,
            ident.store_name,
            ident.legal_name,
            ident.display_name,
            item.business_name,
          ].filter(Boolean).join(' ').toLowerCase();
          return haystack.includes(query);
        }
        if (resourceType === 'customers') {
          return item.name?.toLowerCase().includes(query) ||
                 item.email?.toLowerCase().includes(query);
        }
        if (resourceType === 'orders') {
          return item.order_number?.toLowerCase().includes(query) ||
                 item.customer?.name?.toLowerCase().includes(query) ||
                 item.customer?.email?.toLowerCase().includes(query);
        }
        if (resourceType === 'leads') {
          return item.customer_name?.toLowerCase().includes(query) ||
                 item.customer_email?.toLowerCase().includes(query) ||
                 item.listing_title?.toLowerCase().includes(query);
        }
        return true;
      });
    }
    
    // Date range filter (orders)
    if (resourceType === 'orders' && dateRange !== 'all') {
      const now = new Date();
      const cutoff = new Date();
      if (dateRange === 'today') cutoff.setHours(0, 0, 0, 0);
      else if (dateRange === 'week') cutoff.setDate(now.getDate() - 7);
      else if (dateRange === 'month') cutoff.setDate(now.getDate() - 30);
      
      filtered = filtered.filter(item => new Date(item.created_at || item.order_date) >= cutoff);
    }
    
    // Payment status filter (orders)
    if (resourceType === 'orders' && paymentStatus !== 'all') {
      filtered = filtered.filter(item => item.payment_status?.toLowerCase() === paymentStatus.toLowerCase());
    }

    // Needs Refund Action filter (orders) — surfaces cancellations where the
    // automated Razorpay refund attempt failed/skipped and financial_status
    // was never synced, so admins can't miss them.
    if (resourceType === 'orders' && needsRefundOnly) {
      filtered = filtered.filter(item => item.needs_refund_action === true);
    }
    
    // Vendor-specific filters
    if (resourceType === 'vendors') {
      if (vendorStatus !== 'all') {
        filtered = filtered.filter(item => item.status === vendorStatus);
      }
      if (vendorKyc !== 'all') {
        filtered = filtered.filter(item => {
          const isVerified = item.vendor_profile?.verifications?.gst_verified;
          return vendorKyc === 'verified' ? isVerified : !isVerified;
        });
      }
      if (vendorLocation !== 'all') {
        filtered = filtered.filter(item => {
          const bd = item.vendor_profile?.business_details || {};
          const city = (bd.city || '').toLowerCase();
          const state = (bd.state || '').toLowerCase();
          return city === vendorLocation.toLowerCase() || state === vendorLocation.toLowerCase();
        });
      }
      if (vendorCategory !== 'all') {
        filtered = filtered.filter(item => {
          const bd = item.vendor_profile?.business_details || {};
          const cat = (bd.category || bd.business_category || '').toLowerCase();
          const subCat = (bd.subcategory || bd.business_subcategory || '').toLowerCase();
          return cat === vendorCategory.toLowerCase() || subCat === vendorCategory.toLowerCase();
        });
      }
      if (vendorPlan !== 'all') {
        if (vendorPlan === '__none__') {
          // iter-221g — "No Plan" filter: vendors without any subscription
          // (planless / never selected). Matches the same predicate the
          // bulk-start-trial endpoint uses on the backend.
          filtered = filtered.filter(item => {
            const plan = item.subscription_plan_name;
            return !plan || plan === 'None' || plan === 'No Plan' || plan === '';
          });
        } else {
          filtered = filtered.filter(item => item.subscription_plan_name === vendorPlan);
        }
      }
    }
    
    // Sorting
    // -------------------------------------------------------------------
    // Comparator helpers. `date_desc` / `date_asc` fall back to updated_at
    // and finally to a stable tiebreak on order_number so items with
    // identical or missing dates stay in a deterministic order.
    // -------------------------------------------------------------------
    const _ts = (item) => {
      const d = item?.created_at || item?.order_date || item?.updated_at;
      const t = d ? new Date(d).getTime() : NaN;
      return Number.isFinite(t) ? t : 0;
    };
    const _orderNumTiebreak = (a, b, dir) => {
      const an = (a.order_number || a.id || '').toString();
      const bn = (b.order_number || b.id || '').toString();
      return dir === 'desc' ? bn.localeCompare(an, undefined, { numeric: true })
                            : an.localeCompare(bn, undefined, { numeric: true });
    };

    if (sortBy) {
      filtered.sort((a, b) => {
        switch (sortBy) {
          case 'sales_desc':
            return (b.vendor_profile?.metrics?.total_sales || 0) - (a.vendor_profile?.metrics?.total_sales || 0);
          case 'dispute_asc':
            return (a.vendor_profile?.metrics?.dispute_rate || 0) - (b.vendor_profile?.metrics?.dispute_rate || 0);
          case 'last_activity_desc':
            return new Date(b.last_activity || b.created_at) - new Date(a.last_activity || a.created_at);
          case 'ltv_desc':
            return (b.lifetime_value || 0) - (a.lifetime_value || 0);
          case 'signup_date_desc':
          case 'date_desc': {
            const d = _ts(b) - _ts(a);
            return d !== 0 ? d : _orderNumTiebreak(a, b, 'desc');
          }
          case 'date_asc': {
            const d = _ts(a) - _ts(b);
            return d !== 0 ? d : _orderNumTiebreak(a, b, 'asc');
          }
          case 'total_desc':
            return (b.total_amount || b.pricing?.total || 0) - (a.total_amount || a.pricing?.total || 0);
          case 'total_asc':
            return (a.total_amount || a.pricing?.total || 0) - (b.total_amount || b.pricing?.total || 0);
          case 'name':
          case 'customer':
            return (a.name || a.customer?.name || '').localeCompare(b.name || b.customer?.name || '');
          case 'joining_date_desc':
            return new Date(b.created_at || 0) - new Date(a.created_at || 0);
          case 'joining_date_asc':
            return new Date(a.created_at || 0) - new Date(b.created_at || 0);
          case 'listings_desc':
            return (b.vendor_profile?.metrics?.products_listed || 0) - (a.vendor_profile?.metrics?.products_listed || 0);
          case 'listings_asc':
            return (a.vendor_profile?.metrics?.products_listed || 0) - (b.vendor_profile?.metrics?.products_listed || 0);
          default:
            return 0;
        }
      });
    }
    
    // Admin orders view: group children under their parent and inject
    // visual flags so the row renderer can show indent + chevron + sub-order
    // styling. Only applies for admin (vendors only see their own children
    // server-side, no parents present in their list anyway).
    if (resourceType === 'orders' && isAdmin && filtered.length > 0) {
      const childrenByParent = new Map();
      const others = [];
      for (const o of filtered) {
        if (o.parent_order_id && !o.is_parent_order) {
          if (!childrenByParent.has(o.parent_order_id)) {
            childrenByParent.set(o.parent_order_id, []);
          }
          childrenByParent.get(o.parent_order_id).push(o);
        } else {
          others.push(o);
        }
      }

      // FIX (iter-298k): once children are siphoned into their parent
      // groups, `others` must be re-sorted using the FAMILY effective date
      // (max of parent + children created_at). Otherwise a parent whose
      // own created_at is older than its newest child will sink below a
      // truly older standalone order — breaking "Newest" ordering while
      // "Oldest" happens to look right because the family's earliest
      // date is usually the parent's own timestamp.
      if (sortBy === 'date_desc' || sortBy === 'date_asc') {
        const familyTs = (o) => {
          if (o.is_parent_order) {
            const kids = childrenByParent.get(o.id) || [];
            let best = _ts(o);
            for (const k of kids) {
              const kt = _ts(k);
              if (sortBy === 'date_desc' ? kt > best : kt < best) best = kt;
            }
            return best;
          }
          return _ts(o);
        };
        others.sort((a, b) => {
          const at = familyTs(a);
          const bt = familyTs(b);
          const d = sortBy === 'date_desc' ? bt - at : at - bt;
          return d !== 0 ? d : _orderNumTiebreak(a, b, sortBy === 'date_desc' ? 'desc' : 'asc');
        });
        // Also sort each group's children among themselves so expanded
        // children read in the same chronological direction.
        for (const kids of childrenByParent.values()) {
          kids.sort((a, b) => {
            const d = sortBy === 'date_desc' ? _ts(b) - _ts(a) : _ts(a) - _ts(b);
            return d !== 0 ? d : _orderNumTiebreak(a, b, sortBy === 'date_desc' ? 'desc' : 'asc');
          });
        }
      }

      // Re-flatten: each parent immediately followed by its children (only when expanded);
      // orphaned children (parent filtered out by date/search) keep their position.
      const grouped = [];
      const usedChildren = new Set();
      for (const o of others) {
        // Tag parents with child count + expansion-aware flag
        if (o.is_parent_order) {
          const kids = childrenByParent.get(o.id) || [];
          grouped.push({
            ...o,
            _isExpandableParent: true,
            _childCount: kids.length,
            _isExpanded: expandedParents.has(o.id),
          });
          if (expandedParents.has(o.id)) {
            for (const k of kids) {
              grouped.push({ ...k, _isChild: true, _parentRef: o.id });
              usedChildren.add(k.id);
            }
          } else {
            kids.forEach((k) => usedChildren.add(k.id));
          }
        } else {
          grouped.push(o);
        }
      }
      // Append any orphaned children whose parents weren't in the result set
      // (e.g. searched only for a child's order #) so they're not silently dropped.
      for (const [parentId, kids] of childrenByParent.entries()) {
        for (const k of kids) {
          if (!usedChildren.has(k.id)) {
            grouped.push({ ...k, _isOrphanChild: true, _parentRef: parentId });
          }
        }
      }
      return grouped;
    }

    return filtered;
  }, [data, schema, activeTab, searchQuery, sortBy, dateRange, paymentStatus, needsRefundOnly, getItemTab, resourceType, vendorStatus, vendorKyc, vendorLocation, vendorCategory, vendorPlan, isAdmin, expandedParents]);

  // Total count of orders flagged needs_refund_action (independent of the
  // active tab/search filters) — shown as a badge on the filter toggle.
  const needsRefundCount = useMemo(() => {
    if (resourceType !== 'orders') return 0;
    return data.filter(item => item.needs_refund_action === true).length;
  }, [data, resourceType]);

  // Tab counts
  const getTabCounts = useMemo(() => {
    const counts = { all: data.length };
    schema.tabs?.forEach(tab => {
      if (tab.id !== 'all') {
        counts[tab.id] = data.filter(item => getItemTab(item) === tab.id).length;
      }
    });
    return counts;
  }, [data, schema.tabs, getItemTab]);
  
  // Dynamic vendor filter options (computed from loaded data)
  const vendorFilterOptions = useMemo(() => {
    if (resourceType !== 'vendors') return {};
    const locations = new Set();
    const categories = new Set();
    const plans = new Set();
    
    data.forEach(v => {
      const bd = v.vendor_profile?.business_details || {};
      if (bd.city) locations.add(bd.city);
      if (bd.state) locations.add(bd.state);
      if (bd.category || bd.business_category) categories.add(bd.category || bd.business_category);
      if (bd.subcategory || bd.business_subcategory) categories.add(bd.subcategory || bd.business_subcategory);
      if (v.subscription_plan_name) plans.add(v.subscription_plan_name);
    });
    
    return {
      locations: [...locations].filter(Boolean).sort(),
      categories: [...categories].filter(Boolean).sort(),
      plans: [...plans].filter(Boolean).sort()
    };
  }, [data, resourceType]);
  
  // ============================================================================
  // ACTION HANDLERS
  // ============================================================================
  const handleExport = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${schema.exportEndpoint}`, {
        headers: { 'Authorization': `Bearer ${sessionStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${resourceType}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
      toast({ title: 'Success', description: `${schema.title} exported successfully` });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to export', variant: 'destructive' });
    }
  };
  
  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${schema.importEndpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${sessionStorage.getItem('token')}` },
        body: formData
      });
      
      if (!response.ok) throw new Error('Import failed');
      const result = await response.json();
      
      toast({
        title: 'Import Complete',
        description: `Imported ${result.imported} records. ${result.errors?.length || 0} errors.`
      });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to import', variant: 'destructive' });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  
  // Vendor-specific actions
  const handleActivateDeactivate = async (item, e) => {
    e?.stopPropagation();
    const endpoint = item.status === 'active' 
      ? `/api/vendors/${item.id}/deactivate`
      : `/api/vendors/${item.id}/activate`;
    
    try {
      await apiCall(endpoint, { method: 'PUT' });
      toast({ title: 'Success', description: `Vendor ${item.status === 'active' ? 'deactivated' : 'activated'}` });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };
  
  const handleKycApprove = async (item, e) => {
    e?.stopPropagation();
    const currentStatus = item.vendor_profile?.verifications?.gst_verified;
    if (!window.confirm(currentStatus ? 'Revoke KYC verification?' : 'Approve KYC verification?')) return;
    
    try {
      await apiCall(`/api/vendors/${item.id}/verify-kyc`, {
        method: 'PUT',
        body: JSON.stringify({ gst_verified: !currentStatus })
      });
      toast({ title: 'Success', description: `KYC verification ${currentStatus ? 'revoked' : 'approved'}` });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };
  
  const handleUpdateCommission = async () => {
    try {
      await apiCall(`/api/vendors/${selectedItem.id}/commission?commission_rate=${newCommission}`, { method: 'PUT' });
      toast({ title: 'Success', description: 'Commission updated successfully' });
      setCommissionDialogOpen(false);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };
  
  // Customer-specific actions
  const handleAddCustomer = async () => {
    if (!formData.name || !formData.email || !formData.password) {
      toast({ title: 'Error', description: 'Please fill in all fields', variant: 'destructive' });
      return;
    }
    try {
      await apiCall('/api/customers/', { method: 'POST', body: JSON.stringify(formData) });
      toast({ title: 'Success', description: 'Customer added successfully' });
      setAddDialogOpen(false);
      setFormData({});
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };
  
  const handleEditCustomer = async () => {
    try {
      await apiCall(`/api/customers/${selectedItem.id}?name=${encodeURIComponent(formData.name)}`, { method: 'PUT' });
      toast({ title: 'Success', description: 'Customer updated successfully' });
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };
  
  const handleDeleteCustomer = async () => {
    try {
      await apiCall(`/api/customers/${selectedItem.id}`, { method: 'DELETE' });
      toast({ title: 'Success', description: 'Customer deleted successfully' });
      setDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };
  
  // Lead-specific actions
  const handleUpdateLead = async () => {
    try {
      await apiCall(`/api/leads/${selectedItem.id}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });
      sonnerToast.success('Lead updated successfully');
      setUpdateDialogOpen(false);
      fetchData();
      fetchStats();
    } catch (error) {
      sonnerToast.error('Failed to update lead');
    }
  };
  
  // Order invoice actions
  const handleGenerateInvoice = async (orderId, e) => {
    e?.stopPropagation();
    if (!zohoConnected) {
      toast({ title: 'Zoho Invoice Not Connected', description: 'Please connect in Settings > Integrations', variant: 'destructive' });
      return;
    }
    try {
      setGeneratingInvoice(orderId);
      await apiCall(`/api/zoho/invoices/create-for-order/${orderId}`, { method: 'POST' });
      toast({ title: 'Invoice Created', description: 'Invoice created successfully' });
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setGeneratingInvoice(null);
    }
  };
  
  // ============================================================================
  // BULK ACTION HANDLERS
  // ============================================================================
  const toggleSelectAll = () => {
    if (selectedItems.size === getDisplayData.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(getDisplayData.map(item => item.id)));
    }
  };

  const toggleSelectItem = (id, e) => {
    e?.stopPropagation();
    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
  };

  const clearSelection = () => {
    setSelectedItems(new Set());
  };

  const handleBulkApprove = async () => {
    if (selectedItems.size === 0) return;
    
    setBulkActionLoading(true);
    try {
      const ids = Array.from(selectedItems);
      let successCount = 0;
      
      for (const id of ids) {
        try {
          await apiCall(`/api/admin/vendors/${id}/approve`, { method: 'PUT' });
          successCount++;
        } catch (e) {
          console.error(`Failed to approve vendor ${id}:`, e);
        }
      }
      
      await fetchData();
      setSelectedItems(new Set());
      toast({ title: 'Success', description: `${successCount} vendors approved` });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to approve vendors', variant: 'destructive' });
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkSuspend = async () => {
    if (selectedItems.size === 0) return;
    if (!window.confirm(`Are you sure you want to suspend ${selectedItems.size} vendors?`)) return;
    
    setBulkActionLoading(true);
    try {
      const ids = Array.from(selectedItems);
      let successCount = 0;
      
      for (const id of ids) {
        try {
          await apiCall(`/api/vendors/${id}/deactivate`, { method: 'PUT' });
          successCount++;
        } catch (e) {
          console.error(`Failed to suspend vendor ${id}:`, e);
        }
      }
      
      await fetchData();
      setSelectedItems(new Set());
      toast({ title: 'Success', description: `${successCount} vendors suspended` });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to suspend vendors', variant: 'destructive' });
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkActivate = async () => {
    if (selectedItems.size === 0) return;
    
    setBulkActionLoading(true);
    try {
      const ids = Array.from(selectedItems);
      let successCount = 0;
      
      for (const id of ids) {
        try {
          await apiCall(`/api/vendors/${id}/activate`, { method: 'PUT' });
          successCount++;
        } catch (e) {
          console.error(`Failed to activate vendor ${id}:`, e);
        }
      }
      
      await fetchData();
      setSelectedItems(new Set());
      toast({ title: 'Success', description: `${successCount} vendors activated` });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to activate vendors', variant: 'destructive' });
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedItems.size} items? This action cannot be undone.`)) return;
    
    setBulkActionLoading(true);
    try {
      const ids = Array.from(selectedItems);
      let successCount = 0;
      
      for (const id of ids) {
        try {
          await apiCall(`${schema.apiEndpoint}${id}`, { method: 'DELETE' });
          successCount++;
        } catch (e) {
          console.error(`Failed to delete item ${id}:`, e);
        }
      }
      
      await fetchData();
      setSelectedItems(new Set());
      toast({ title: 'Success', description: `${successCount} items deleted` });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete items', variant: 'destructive' });
    } finally {
      setBulkActionLoading(false);
    }
  };
  
  // ============================================================================
  // BULK INVOICE DOWNLOAD (Orders)
  // ============================================================================
  const handleBulkInvoiceDownload = async () => {
    if (selectedItems.size === 0) return;
    setBulkActionLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/bulk-download`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`
        },
        body: JSON.stringify({ order_ids: Array.from(selectedItems) })
      });
      if (!response.ok) throw new Error('Failed to generate invoices');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoices_${new Date().toISOString().slice(0,10)}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Success', description: `Downloaded ${selectedItems.size} invoices as ZIP` });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to download invoices', variant: 'destructive' });
    } finally {
      setBulkActionLoading(false);
    }
  };

  // ============================================================================
  // TRIAL EXTENSION HANDLERS
  // ============================================================================
  const openTrialDialog = (vendor = null, e) => {
    e?.stopPropagation();
    setTrialTargetVendor(vendor);
    setTrialDate(vendor?.trial_end ? new Date(vendor.trial_end) : null);
    setTrialDialogOpen(true);
  };

  // iter-221g — Start Professional Trial (single + bulk).
  // Different from "Extend Trial": this targets vendors who have NO
  // subscription / no plan and auto-enrols them in Professional + 30d
  // trial. Idempotent — vendors already on a plan are reported as
  // already_enrolled and not re-charged.
  // Bulk "Until Sale/Lead" — single unified event-based trial activation.
  // Tier-1 (enquiry-only) vendors activate on first lead.
  // Tier-2 vendors activate on first sale OR first lead.
  const handleBulkExtendUntilSale = async (e) => {
    e?.stopPropagation();
    const ids = Array.from(selectedItems);
    if (ids.length === 0) return;
    if (!window.confirm(
      `Set ${ids.length} vendor(s) to "trial until first sale/lead" (max 6-month cap)?\n\n` +
      `• Tier-1 vendors will activate on their first lead/enquiry.\n` +
      `• Tier-2 vendors will activate on their first sale or lead.`
    )) return;

    setBulkActionLoading(true);
    try {
      const res = await apiCall('/api/vendors/bulk-extend-until-sale', {
        method: 'POST',
        body: JSON.stringify({ vendor_ids: ids }),
      });
      toast({
        title: 'Until Sale/Lead applied',
        description: res?.message || `${res?.ok_count ?? 0} updated, ${res?.skipped_count ?? 0} skipped, ${res?.failed_count ?? 0} failed`,
      });
      setSelectedItems(new Set());
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to apply until-sale extension',
        variant: 'destructive',
      });
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleStartProfessionalTrial = async (vendor = null, e) => {
    e?.stopPropagation();
    const ids = vendor ? [vendor.id] : Array.from(selectedItems);
    if (ids.length === 0) return;
    const label = vendor
      ? `Start a Professional 30-day trial for ${vendor.name || 'this vendor'}?`
      : `Start a Professional 30-day trial for ${ids.length} vendor(s)? Vendors who already have a plan will be skipped.`;
    if (!window.confirm(label)) return;

    setBulkActionLoading(true);
    try {
      const res = await apiCall('/api/vendor-subscription/admin/bulk-start-trial', {
        method: 'POST',
        body: JSON.stringify({ vendor_ids: ids, reason: 'admin_bulk_action' }),
      });
      const enrolled = res?.enrolled_count ?? 0;
      const already = res?.already_enrolled_count ?? 0;
      const failed = res?.failed_count ?? 0;
      toast({
        title: 'Trial enrolment',
        description: `Enrolled ${enrolled} • Already on plan ${already} • Failed ${failed}`,
      });
      if (!vendor) setSelectedItems(new Set());
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to start professional trial',
        variant: 'destructive',
      });
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleTrialExtension = async () => {
    if (!trialDate) {
      toast({ title: 'Error', description: 'Please select a date', variant: 'destructive' });
      return;
    }
    setTrialSaving(true);
    try {
      const isoDate = trialDate.toISOString();
      if (trialTargetVendor) {
        // Single vendor
        await apiCall(`/api/vendors/${trialTargetVendor.id}/trial-extension`, {
          method: 'PUT',
          body: JSON.stringify({ trial_end_date: isoDate })
        });
        toast({ title: 'Success', description: `Trial extended for ${trialTargetVendor.name}` });
      } else {
        // Bulk
        const ids = Array.from(selectedItems);
        await apiCall('/api/vendors/bulk-trial-extension', {
          method: 'PUT',
          body: JSON.stringify({ vendor_ids: ids, trial_end_date: isoDate })
        });
        toast({ title: 'Success', description: `Trial extended for ${ids.length} vendor(s)` });
        setSelectedItems(new Set());
      }
      setTrialDialogOpen(false);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to extend trial', variant: 'destructive' });
    } finally {
      setTrialSaving(false);
    }
  };

  // ============================================================================
  // BULK CHANGE PLAN
  // ============================================================================
  const handleBulkChangePlan = async () => {
    if (!selectedPlanCode) {
      toast({ title: 'Select a plan', description: 'Please choose a subscription plan', variant: 'destructive' });
      return;
    }
    setPlanChangeSaving(true);
    try {
      const ids = Array.from(selectedItems);
      const res = await apiCall('/api/vendors/bulk-change-plan', {
        method: 'PUT',
        body: JSON.stringify({
          vendor_ids: ids,
          plan_code: selectedPlanCode,
          reason: planChangeReason || 'admin_bulk_plan_change',
        }),
      });
      const plan = PLAN_OPTIONS.find(p => p.code === selectedPlanCode);
      sonnerToast.success(
        `Plan changed to ${plan?.label} (${plan?.commission}%) for ${res.updated_count} vendor(s)`,
        { duration: 5000 }
      );
      setSelectedItems(new Set());
      setPlanDialogOpen(false);
      setSelectedPlanCode('');
      setPlanChangeReason('');
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to change plan', variant: 'destructive' });
    } finally {
      setPlanChangeSaving(false);
    }
  };

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================
  const renderStatusBadge = (status) => {
    const configs = {
      active: { label: 'Active', className: 'bg-green-100 text-green-800' },
      pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-800' },
      suspended: { label: 'Suspended', className: 'bg-red-100 text-red-800' },
      terminated: { label: 'Terminated', className: 'bg-red-600 text-white' },
      banned: { label: 'Banned', className: 'bg-red-600 text-white' }
    };
    const config = configs[status?.toLowerCase()] || { label: status, className: 'bg-gray-100 text-gray-800' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };
  
  const renderOrderStatusBadge = (status) => {
    const configs = {
      new: { label: 'New', class: 'bg-blue-100 text-blue-800' },
      payment_confirmed: { label: 'Payment Confirmed', class: 'bg-green-100 text-green-800' },
      processing: { label: 'Processing', class: 'bg-yellow-100 text-yellow-800' },
      shipped: { label: 'Shipped', class: 'bg-purple-100 text-purple-800' },
      out_for_delivery: { label: 'Out for Delivery', class: 'bg-indigo-100 text-indigo-800' },
      delivered: { label: 'Delivered', class: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Cancelled', class: 'bg-red-100 text-red-800' },
      return_requested: { label: 'Return Requested', class: 'bg-orange-100 text-orange-800' },
      refunded: { label: 'Refunded', class: 'bg-gray-100 text-gray-800' },
      payment_failed: { label: 'Payment Failed', class: 'bg-red-100 text-red-800' }
    };
    const config = configs[status?.toLowerCase()] || { label: status, class: 'bg-gray-100 text-gray-800' };
    return <Badge className={config.class}>{config.label}</Badge>;
  };
  
  const renderLeadStatusBadge = (status) => {
    const configs = {
      new: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'New' },
      contacted: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Contacted' },
      qualified: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Qualified' },
      converted: { bg: 'bg-green-100', text: 'text-green-800', label: 'Converted' },
      lost: { bg: 'bg-red-100', text: 'text-red-800', label: 'Lost' }
    };
    const config = configs[status] || configs.new;
    return <Badge className={`${config.bg} ${config.text}`}>{config.label}</Badge>;
  };
  
  const renderPriorityBadge = (priority) => {
    const configs = {
      low: { bg: 'bg-gray-100', text: 'text-gray-800' },
      medium: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
      high: { bg: 'bg-red-100', text: 'text-red-800' }
    };
    const config = configs[priority] || configs.medium;
    return <Badge className={`${config.bg} ${config.text}`}>{priority?.toUpperCase()}</Badge>;
  };
  
  const renderCellContent = (item, column) => {
    const value = column.accessor ? getNestedValue(item, column.accessor) : item[column.key];
    
    switch (column.render) {
      case 'vendor_info':
        return (
          <div>
            <div className="font-medium">{item.name || 'N/A'}</div>
            <div className="text-sm text-gray-500">{item.email}</div>
            {/* Health badges — render only when /health-flags joined data */}
            {item._health && (
              <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`vendor-health-badges-${item.id}`}>
                {item._health.address_complete ? (
                  <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="badge-address-complete">
                    <CheckCircle className="w-2.5 h-2.5 mr-0.5" /> Address
                  </span>
                ) : (
                  <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200" data-testid="badge-address-missing">
                    <AlertCircle className="w-2.5 h-2.5 mr-0.5" /> Address Missing
                  </span>
                )}
                {item._health.delhivery_registered ? (
                  <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="badge-delhivery-registered">
                    <CheckCircle className="w-2.5 h-2.5 mr-0.5" /> Delhivery
                  </span>
                ) : (
                  <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200" data-testid="badge-delhivery-pending">
                    <Clock className="w-2.5 h-2.5 mr-0.5" /> Delhivery Pending
                  </span>
                )}
                {item._health.bank_complete && (
                  <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200" data-testid="badge-bank-complete">
                    <CheckCircle className="w-2.5 h-2.5 mr-0.5" /> Bank
                  </span>
                )}
                {item._health.gst_verified && (
                  <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200" data-testid="badge-gst-verified">
                    <CheckCircle className="w-2.5 h-2.5 mr-0.5" /> GST
                  </span>
                )}
                {/* iter-231-S / iter-231-U — Razorpay Route Linked Account chip.
                    Reads the SSOT-resolved state field first (preferred), falls
                    back to the _health flags for legacy export endpoints. */}
                {(() => {
                  const ssotState = item.ssot_razorpay_la_state;
                  const isActive = ssotState === 'active'
                    || item._health?.razorpay_la_activated;
                  const isPending = ssotState === 'pending'
                    || (item._health?.razorpay_la_present && !item._health?.razorpay_la_activated);
                  if (isActive) {
                    return (
                      <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="badge-razorpay-la-active">
                        <CheckCircle className="w-2.5 h-2.5 mr-0.5" /> RZP LA
                      </span>
                    );
                  }
                  if (isPending) {
                    return (
                      <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200" data-testid="badge-razorpay-la-pending">
                        <Clock className="w-2.5 h-2.5 mr-0.5" /> RZP LA Pending
                      </span>
                    );
                  }
                  return (
                    <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200" data-testid="badge-razorpay-la-missing">
                      <AlertCircle className="w-2.5 h-2.5 mr-0.5" /> RZP LA Missing
                    </span>
                  );
                })()}
              </div>
            )}
          </div>
        );
      
      case 'customer_info':
        return (
          <div>
            <div className="font-medium">{item.customer?.name || 'N/A'}</div>
            <div className="text-sm text-gray-500">{item.customer?.email}</div>
          </div>
        );
      
      case 'lead_customer':
        return (
          <div data-testid={item.source === 'request_for_booking' ? `lead-row-booking-request-${item.id}` : `lead-row-${item.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium">{item.customer_name}</span>
              {item.otp_verified && <CheckCircle className="h-4 w-4 text-green-500" />}
              {item.source === 'request_for_booking' && (
                <Badge
                  className="bg-amber-100 text-amber-900 border border-amber-200 text-[10px] font-semibold"
                  title="Customer picked dates + amount and is waiting for vendor approval"
                >
                  🎯 Booking Request
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
              <Mail className="h-3 w-3" />{item.customer_email}
            </div>
            {item.customer_phone && (
              <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                <Phone className="h-3 w-3" />{item.customer_phone}
              </div>
            )}
            {item.source === 'request_for_booking' && item.form_data?.booking_number && (
              <button
                type="button"
                className="mt-1 inline-flex items-center gap-1 text-[11px] text-purple-700 hover:text-purple-900 hover:underline"
                onClick={(e) => {
                  e.stopPropagation();
                  if (item.form_data?.booking_id) {
                    navigate(`/bookings/${item.form_data.booking_id}`);
                  }
                }}
                data-testid={`view-booking-from-lead-${item.id}`}
              >
                <ExternalLink className="h-3 w-3" />
                View booking → {item.form_data.booking_number}
              </button>
            )}
          </div>
        );
      
      case 'lead_listing':
        return (
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-gray-400" />
            <div>
              <p className="font-medium text-sm">{item.listing_title}</p>
              <p className="text-xs text-gray-500">{item.listing_id}</p>
            </div>
          </div>
        );
      
      case 'lead_type':
        return (
          <div className="space-y-1">
            <Badge variant="outline">{item.listing_type}</Badge>
            <Badge className="bg-purple-100 text-purple-800 text-xs block w-fit">{item.lead_type}</Badge>
          </div>
        );
      
      case 'lead_status':
        return renderLeadStatusBadge(item.status);
      
      case 'lead_date':
        return (
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <CalendarIcon className="h-3 w-3" />
              {new Date(item.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </div>
            {item.laas_fee_charged && <div className="text-xs text-green-600">₹{item.laas_fee} charged</div>}
          </div>
        );
      
      case 'status_badge':
        return renderStatusBadge(item.status);
      
      case 'order_status_badge':
        return (
          <div className="flex flex-col gap-1 items-start">
            {renderOrderStatusBadge(item.status)}
            {item.needs_refund_action && (
              <Badge
                className="bg-red-100 text-red-800 border border-red-300 text-[10px] font-semibold whitespace-nowrap"
                data-testid={`order-needs-refund-badge-${item.id}`}
              >
                <AlertTriangle className="h-3 w-3 mr-1 inline" />
                Needs Refund Action
              </Badge>
            )}
          </div>
        );

      case 'awb_info': {
        // Verbose shipping cell — AWB number + tracking link + auth-aware label download.
        // Hidden when no AWB has been generated to keep the table clean.
        const awb = item.awb_number || item.shipping?.awb || item.tracking?.awb;
        if (!awb) {
          return <span className="text-xs text-gray-400 italic">—</span>;
        }
        const trackingUrl = item.tracking_url
          || item.shipping?.tracking_url
          || `https://www.delhivery.com/track-v2/package/${awb}`;
        const labelPath = item.label_url
          || item.shipping?.label_url
          || (awb ? `/api/delhivery/shipments/${item.id}/label` : null);
        return (
          <div className="flex items-center gap-2 text-xs" data-testid={`awb-cell-${item.id}`}>
            <span className="font-mono text-blue-700 font-semibold whitespace-nowrap" title={`AWB ${awb}`}>{awb}</span>
            <a
              href={trackingUrl}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-blue-600 hover:text-blue-800"
              title="Track shipment"
              data-testid={`awb-track-${item.id}`}
            >
              <Truck className="h-3.5 w-3.5" />
            </a>
            {labelPath && (
              <button
                type="button"
                title="Download shipping label"
                data-testid={`awb-label-${item.id}`}
                className="text-indigo-600 hover:text-indigo-800"
                onClick={async (e) => {
                  e.stopPropagation();
                  const { downloadAuthedFile } = await import('../utils/downloadAuthedFile');
                  const res = await downloadAuthedFile(labelPath, `${item.order_number || item.id}_label.pdf`);
                  if (!res.ok) {
                    toast({
                      title: 'Could not download label',
                      description: res.status === 401 ? 'Session expired — please refresh.' : (res.error || 'Try again from Pack & Ship.'),
                      variant: 'destructive',
                    });
                  }
                }}
              >
                <FileText className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        );
      }

      case 'priority_badge':
        return renderPriorityBadge(item.priority);
      
      case 'kyc_badge':
        return item.vendor_profile?.verifications?.gst_verified
          ? <Badge className="bg-green-600 text-white"><CheckCircle className="h-3 w-3 mr-1" />Verified</Badge>
          : <Badge className="bg-yellow-100 text-yellow-800"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      
      case 'dispute_badge':
        const rate = value || 0;
        return <Badge className={rate < 5 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>{rate.toFixed(1)}%</Badge>;
      
      case 'commission':
        return (
          <span className="font-semibold text-purple-600">{value || 10}%</span>
        );
      
      case 'currency':
        return <span className="font-semibold text-green-600">{formatCurrency(value || item.pricing?.total)}</span>;
      
      case 'order_id':
        return <span className="font-medium text-blue-600">{item.order_number || item.id?.substring(0, 8)}</span>;
      
      case 'item_count':
        return <span className="text-sm">{item.items?.length || 0} item{item.items?.length !== 1 ? 's' : ''}</span>;
      
      case 'type_badge':
        return <Badge variant="outline" className="capitalize text-xs">{item.order_type || 'Order'}</Badge>;
      
      case 'date':
        return <span className="text-sm">{formatDate(value)}</span>;
      
      case 'date_with_ago':
        return (
          <div className="text-sm">
            <div>{formatDate(value)}</div>
            <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
              <Clock className="h-3 w-3" />{daysSince(value)} days ago
            </div>
          </div>
        );
      
      case 'date_with_updated':
        return (
          <div className="text-sm">
            <div>{formatDate(item.created_at || item.order_date)}</div>
            <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
              <Clock className="h-3 w-3" />Updated {formatDate(item.updated_at)}
            </div>
          </div>
        );
      
      case 'number':
        return <span className="text-center">{value || 0}</span>;
      
      case 'subscription_plan': {
        if (!value) return <span className="text-gray-400 text-sm">No Plan</span>;
        const planColors = {
          'Launch': 'bg-gray-100 text-gray-700',
          'Basic': 'bg-blue-100 text-blue-700',
          'Professional': 'bg-indigo-100 text-indigo-700',
          'Pro': 'bg-indigo-100 text-indigo-700',
          'Business': 'bg-purple-100 text-purple-700',
          'Enterprise': 'bg-amber-100 text-amber-800',
          'Free': 'bg-gray-100 text-gray-600',
        };
        const colorClass = planColors[value] || 'bg-gray-100 text-gray-700';
        return (
          <Badge className={`${colorClass} font-medium text-xs`} data-testid={`vendor-plan-${item.id}`}>
            <Crown className="h-3 w-3 mr-1" />{value}
          </Badge>
        );
      }
      
      case 'trial_end_date': {
        const trialEnd = item.trial_end;
        const subStatus = item.subscription_status;
        if (!trialEnd && subStatus !== 'trial') {
          return <span className="text-gray-400 text-sm" data-testid={`vendor-trial-${item.id}`}>--</span>;
        }
        const date = trialEnd ? new Date(trialEnd) : null;
        const isExpired = date && date < new Date();
        const daysLeft = date ? Math.ceil((date - new Date()) / (1000 * 60 * 60 * 24)) : null;
        return (
          <button
            onClick={(e) => openTrialDialog(item, e)}
            className="group flex items-center gap-1.5 text-sm hover:bg-gray-100 rounded px-2 py-1 -mx-2 transition-colors"
            data-testid={`vendor-trial-edit-${item.id}`}
          >
            <CalendarClock className="h-3.5 w-3.5 text-gray-400 group-hover:text-blue-500" />
            {date ? (
              <span className={isExpired ? 'text-red-600' : daysLeft <= 7 ? 'text-amber-600' : 'text-gray-700'}>
                {date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                {!isExpired && daysLeft !== null && <span className="text-xs ml-1 text-gray-500">({daysLeft}d)</span>}
                {isExpired && <span className="text-xs ml-1">(expired)</span>}
              </span>
            ) : (
              <span className="text-gray-400">Set date</span>
            )}
            <Edit className="h-3 w-3 text-gray-300 group-hover:text-blue-500 ml-auto" />
          </button>
        );
      }

      default:
        return value || 'N/A';
    }
  };
  
  const renderRowActions = (item) => {
    const actions = schema.actions || [];
    
    // If no actions, don't render anything
    if (actions.length === 0 && resourceType !== 'orders') return null;
    
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-8 w-8 p-0"
            data-testid={`${resourceType}-actions-${item.id}`}
            onClick={(e) => e.stopPropagation()}
          >
            <MoreVertical className="h-4 w-4" />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {/* View Action */}
          {actions.includes('view') && (
            <DropdownMenuItem
              data-testid={`${resourceType}-view-${item.id}`}
              onClick={(e) => {
                e.stopPropagation();
                if (schema.detailRoute) navigate(`${schema.detailRoute}/${item.id}`);
                else { setSelectedItem(item); setViewDialogOpen(true); }
              }}
            >
              <Eye className="h-4 w-4 mr-2" />
              View Details
            </DropdownMenuItem>
          )}
          
          {/* View Listings Action (Vendors only) */}
          {actions.includes('view_listings') && resourceType === 'vendors' && (
            <DropdownMenuItem
              data-testid={`${resourceType}-view-listings-${item.id}`}
              onClick={(e) => {
                e.stopPropagation();
                // Navigate to listings page filtered by vendor
                navigate(`/listings?vendor_id=${item.id}&vendor_name=${encodeURIComponent(item.name || item.vendor_profile?.business_details?.business_name || 'Vendor')}`);
              }}
            >
              <Package className="h-4 w-4 mr-2" />
              View Listings
            </DropdownMenuItem>
          )}

          {/* iter-214 — View Billing (admin side). Jumps straight into
              the AdminVendorBilling dossier for this vendor. */}
          {actions.includes('view_billing') && resourceType === 'vendors' && (
            <DropdownMenuItem
              data-testid={`${resourceType}-view-billing-${item.id}`}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/admin/vendors/billing/${item.id}`);
              }}
            >
              <CreditCard className="h-4 w-4 mr-2" />
              View Billing
            </DropdownMenuItem>
          )}

          {/* Send Message (Vendors only) — bell icon + push notification */}
          {actions.includes('send_message') && resourceType === 'vendors' && (
            <DropdownMenuItem
              data-testid={`${resourceType}-send-message-${item.id}`}
              onClick={(e) => {
                e.stopPropagation();
                setMessageTarget(item);
                setMessageForm({ title: '', message: '', priority: 'high', send_push: true, send_email: false });
                setMessageDialogOpen(true);
              }}
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Send Message
            </DropdownMenuItem>
          )}
          
          {/* Edit Action */}
          {actions.includes('edit') && (
            <DropdownMenuItem
              data-testid={`${resourceType}-edit-${item.id}`}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedItem(item);
                setFormData({ name: item.name, email: item.email });
                setEditDialogOpen(true);
              }}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </DropdownMenuItem>
          )}
          
          {/* KYC Actions */}
          {actions.includes('kyc_approve') && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid={`${resourceType}-kyc-approve-${item.id}`}
                onClick={(e) => handleKycApprove(item, e)}
                className="text-green-600"
              >
                <UserCheck className="h-4 w-4 mr-2" />
                Approve KYC
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid={`${resourceType}-kyc-reject-${item.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  // For now, same as approve but could be different action
                  toast({ title: 'Reject KYC', description: 'KYC rejection feature coming soon' });
                }}
                className="text-red-600"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Reject KYC
              </DropdownMenuItem>
            </>
          )}
          
          {/* Activate/Deactivate */}
          {actions.includes('activate_deactivate') && (
            <>
              <DropdownMenuSeparator />
              {item.status === 'active' ? (
                <DropdownMenuItem
                  data-testid={`${resourceType}-deactivate-${item.id}`}
                  onClick={(e) => handleActivateDeactivate(item, e)}
                  className="text-red-600"
                >
                  <UserX className="h-4 w-4 mr-2" />
                  Deactivate Vendor
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  data-testid={`${resourceType}-activate-${item.id}`}
                  onClick={(e) => handleActivateDeactivate(item, e)}
                  className="text-green-600"
                >
                  <UserCheck className="h-4 w-4 mr-2" />
                  Activate Vendor
                </DropdownMenuItem>
              )}
            </>
          )}

          {/* iter-221g — Start Professional Trial (per-row).
              Idempotent: if the vendor already has an active/trial sub
              the backend skips them — useful when an admin is unsure
              whether the vendor is already on a plan. */}
          {resourceType === 'vendors' && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid={`${resourceType}-start-trial-${item.id}`}
                onClick={(e) => handleStartProfessionalTrial(item, e)}
                className="text-purple-700"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Start Professional Trial
              </DropdownMenuItem>
            </>
          )}
          
          {/* Commission */}
          {actions.includes('commission') && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid={`${resourceType}-commission-${item.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedItem(item);
                  setNewCommission(item.vendor_profile?.commission?.rate || '10');
                  setCommissionDialogOpen(true);
                }}
              >
                <DollarSign className="h-4 w-4 mr-2" />
                Set Commission
              </DropdownMenuItem>
            </>
          )}
          
          {/* iter-235 SSOT — Shipment CTAs from canonical matrix.
              The same actions render here (Orders List row) and on the
              Order Details page. No surface invents its own state. */}
          {resourceType === 'orders' && (
            <ShipmentRowActions
              order={item}
              navigate={navigate}
              toast={toast}
            />
          )}

          {/* Sprint-D: Process Refund — shown for cancelled paid orders */}
          {resourceType === 'orders' &&
            (item.status === 'cancelled' || item.status === 'canceled') &&
            (item.financial_status === 'paid' || item.financial_status === 'partially_paid') &&
            item.refund_status !== 'completed' && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid={`order-process-refund-${item.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/orders/${item.id}#refund`);
                }}
                className="text-orange-600"
              >
                <CreditCard className="h-4 w-4 mr-2" />
                Process Refund
              </DropdownMenuItem>
            </>
          )}

          {/* Update (for leads/orders) */}
          {actions.includes('update') && (
            <DropdownMenuItem
              data-testid={`${resourceType}-update-${item.id}`}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedItem(item);
                setUpdateData({ status: item.status, priority: item.priority, notes: item.notes || '' });
                setUpdateDialogOpen(true);
              }}
            >
              <Edit className="h-4 w-4 mr-2" />
              Update Status
            </DropdownMenuItem>
          )}
          
          {/* Invoice */}
          {actions.includes('invoice') && zohoConnected && (
            <DropdownMenuItem
              data-testid={`${resourceType}-invoice-${item.id}`}
              onClick={(e) => handleGenerateInvoice(item.id, e)}
              disabled={generatingInvoice === item.id}
            >
              {generatingInvoice === item.id ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileText className="h-4 w-4 mr-2" />
              )}
              Generate Invoice
            </DropdownMenuItem>
          )}
          
          {/* Delete */}
          {actions.includes('delete') && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid={`${resourceType}-delete-${item.id}`}
                onClick={(e) => { e.stopPropagation(); setSelectedItem(item); setDeleteDialogOpen(true); }}
                className="text-red-600"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };
  
  // ============================================================================
  // RENDER
  // ============================================================================
  if (loading && data.length === 0) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64" data-testid={`${resourceType}-loading`}>
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid={`universal-resource-table-${resourceType}`}>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{schema.title}</h1>
            <p className="text-sm md:text-base text-gray-600 mt-1">{schema.subtitle}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {schema.hasExport && (!schema.exportSuperAdminOnly || user?.role === 'super_admin') && (
              <Button variant="outline" size="sm" onClick={handleExport} data-testid={`${resourceType}-export-btn`}>
                <Download className="h-4 w-4 mr-2" />Export
              </Button>
            )}
            {schema.hasImport && (
              <>
                <input type="file" ref={fileInputRef} onChange={handleImport} accept=".csv" className="hidden" />
                <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} data-testid={`${resourceType}-import-btn`}>
                  <Upload className="h-4 w-4 mr-2" />Import
                </Button>
              </>
            )}
            {schema.hasCreate && (
              <Button size="sm" onClick={() => schema.createRoute ? navigate(schema.createRoute) : setAddDialogOpen(true)}
                data-testid={`${resourceType}-create-btn`}>
                <Plus className="h-4 w-4 mr-2" />Add {schema.title.slice(0, -1)}
              </Button>
            )}
            {resourceType === 'orders' && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleSyncShopifyOrders} 
                disabled={syncingOrders}
                data-testid="sync-shopify-orders-btn"
              >
                {syncingOrders ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}
                {syncingOrders ? 'Syncing...' : 'Sync from Shopify'}
              </Button>
            )}
          </div>
        </div>
        
        {/* Stats Cards (Leads) */}
        {schema.hasStats && stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid={`${resourceType}-stats`}>
            {schema.statsConfig.map((stat) => {
              const Icon = stat.icon;
              const value = stats[stat.key];
              return (
                <Card key={stat.key}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">{stat.label}</p>
                        <p className="text-2xl font-bold mt-1">
                          {stat.decimals ? value?.toFixed(stat.decimals) : value}{stat.suffix || ''}
                        </p>
                      </div>
                      <Icon className={`h-8 w-8 text-${stat.color}-500`} />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
        
        {/* Tabs */}
        {schema.hasTabs && schema.tabs && (
          <Card>
            <CardContent className="p-4 md:p-6">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
                  <TabsList className={`inline-flex w-full justify-start md:grid md:grid-cols-${schema.tabs.length} gap-1 min-w-max`}>
                    {schema.tabs.map((tab) => {
                      const Icon = tab.icon;
                      return (
                        <TabsTrigger key={tab.id} value={tab.id} data-testid={`${resourceType}-tab-${tab.id}`}
                          className="flex items-center gap-1 text-xs md:text-sm whitespace-nowrap px-3 py-2">
                          <Icon className="h-3 w-3 md:h-4 md:w-4" />
                          <span>{tab.label} ({getTabCounts[tab.id] || 0})</span>
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>
                </div>
              </Tabs>
            </CardContent>
          </Card>
        )}
        
        {/* Search & Filters - Hidden on mobile for orders/leads (replaced by simplified mobile header) */}
        <Card className={isMobile && (resourceType === 'orders' || resourceType === 'leads') ? 'hidden' : ''}>
          <CardContent className="p-4 md:p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input placeholder={`Search ${schema.title.toLowerCase()}...`} className="pl-10"
                  value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid={`${resourceType}-search-input`} />
              </div>
              
              {/* Extra Filters */}
              {schema.extraFilters?.includes('vendorStatus') && (
                <Select value={vendorStatus} onValueChange={setVendorStatus}>
                  <SelectTrigger className="w-full md:w-[130px]" data-testid="vendors-status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              )}
              
              {schema.extraFilters?.includes('vendorKyc') && (
                <Select value={vendorKyc} onValueChange={setVendorKyc}>
                  <SelectTrigger className="w-full md:w-[140px]" data-testid="vendors-kyc-filter">
                    <SelectValue placeholder="KYC" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All KYC</SelectItem>
                    <SelectItem value="verified">KYC Approved</SelectItem>
                    <SelectItem value="pending">KYC Pending</SelectItem>
                  </SelectContent>
                </Select>
              )}
              
              {schema.extraFilters?.includes('vendorLocation') && vendorFilterOptions.locations?.length > 0 && (
                <Select value={vendorLocation} onValueChange={setVendorLocation}>
                  <SelectTrigger className="w-full md:w-[140px]" data-testid="vendors-location-filter">
                    <SelectValue placeholder="Location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Locations</SelectItem>
                    {vendorFilterOptions.locations.map(loc => (
                      <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              {schema.extraFilters?.includes('vendorCategory') && vendorFilterOptions.categories?.length > 0 && (
                <Select value={vendorCategory} onValueChange={setVendorCategory}>
                  <SelectTrigger className="w-full md:w-[150px]" data-testid="vendors-category-filter">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {vendorFilterOptions.categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              {schema.extraFilters?.includes('vendorPlan') && (
                <Select value={vendorPlan} onValueChange={setVendorPlan}>
                  <SelectTrigger className="w-full md:w-[140px]" data-testid="vendors-plan-filter">
                    <SelectValue placeholder="Plan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Plans</SelectItem>
                    <SelectItem value="__none__" data-testid="vendors-plan-filter-none">
                      No Plan (planless)
                    </SelectItem>
                    {(vendorFilterOptions.plans || []).map(plan => (
                      <SelectItem key={plan} value={plan}>{plan}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              {schema.extraFilters?.includes('dateRange') && (
                <Select value={dateRange} onValueChange={setDateRange}>
                  <SelectTrigger className="w-full md:w-[140px]" data-testid={`${resourceType}-date-filter`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="week">Last 7 Days</SelectItem>
                    <SelectItem value="month">Last 30 Days</SelectItem>
                  </SelectContent>
                </Select>
              )}
              
              {schema.extraFilters?.includes('paymentStatus') && (
                <Select value={paymentStatus} onValueChange={setPaymentStatus}>
                  <SelectTrigger className="w-full md:w-[120px]" data-testid={`${resourceType}-payment-filter`}>
                    <SelectValue placeholder="Payment" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
              )}

              {resourceType === 'orders' && isAdmin && (
                <Button
                  type="button"
                  variant={needsRefundOnly ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setNeedsRefundOnly(v => !v)}
                  className={needsRefundOnly
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'border-red-300 text-red-700 hover:bg-red-50'}
                  data-testid="orders-needs-refund-filter-toggle"
                >
                  <AlertTriangle className="h-4 w-4 mr-1" />
                  Needs Refund Action{needsRefundCount > 0 ? ` (${needsRefundCount})` : ''}
                </Button>
              )}
              
              {schema.extraFilters?.includes('leadStatus') && (
                <Select value={leadStatus} onValueChange={setLeadStatus}>
                  <SelectTrigger className="w-full md:w-[150px]" data-testid={`${resourceType}-status-filter`}>
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="qualified">Qualified</SelectItem>
                    <SelectItem value="converted">Converted</SelectItem>
                    <SelectItem value="lost">Lost</SelectItem>
                  </SelectContent>
                </Select>
              )}
              
              {schema.extraFilters?.includes('leadType') && (
                <Select value={leadType} onValueChange={setLeadType}>
                  <SelectTrigger className="w-full md:w-[150px]" data-testid={`${resourceType}-type-filter`}>
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="product">Products</SelectItem>
                    <SelectItem value="service">Services</SelectItem>
                    <SelectItem value="rental">Rentals</SelectItem>
                    <SelectItem value="subscription">Subscriptions</SelectItem>
                    <SelectItem value="event">Events</SelectItem>
                    <SelectItem value="classified">Classifieds</SelectItem>
                    <SelectItem value="volunteering">Volunteering</SelectItem>
                  </SelectContent>
                </Select>
              )}
              
              {schema.sortOptions?.length > 0 && (
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-full md:w-[150px]" data-testid={`${resourceType}-sort`}>
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    {schema.sortOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              {schema.hasFilterDrawer && (
                <Button variant="outline" onClick={() => setFilterDrawerOpen(true)} data-testid={`${resourceType}-filters-btn`}>
                  <SlidersHorizontal className="h-4 w-4 mr-2" />Filters
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Bulk Actions Bar - Hidden on mobile */}
        {selectedItems.size > 0 && !isMobile && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <CheckSquare className="h-5 w-5 text-indigo-600" />
              <span className="text-sm font-medium text-indigo-800">
                {selectedItems.size} {resourceType === 'vendors' ? 'vendor' : 'item'}{selectedItems.size > 1 ? 's' : ''} selected
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearSelection}
                className="text-indigo-600 hover:text-indigo-800"
              >
                <XSquare className="h-4 w-4 mr-1" />
                Clear
              </Button>
            </div>
            <div className="flex items-center gap-2">
              {resourceType === 'vendors' && (
                <>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleBulkApprove}
                    disabled={bulkActionLoading}
                    className="border-green-300 text-green-700 hover:bg-green-50"
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Approve
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleBulkActivate}
                    disabled={bulkActionLoading}
                    className="border-blue-300 text-blue-700 hover:bg-blue-50"
                  >
                    <UserCheck className="h-4 w-4 mr-1" />
                    Activate
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleBulkSuspend}
                    disabled={bulkActionLoading}
                    className="border-yellow-300 text-yellow-700 hover:bg-yellow-50"
                  >
                    <Ban className="h-4 w-4 mr-1" />
                    Suspend
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={(e) => openTrialDialog(null, e)}
                    disabled={bulkActionLoading}
                    className="border-teal-300 text-teal-700 hover:bg-teal-50"
                    data-testid="bulk-extend-trial-btn"
                  >
                    <CalendarClock className="h-4 w-4 mr-1" />
                    Extend Trial
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBulkExtendUntilSale}
                    disabled={bulkActionLoading}
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                    data-testid="bulk-until-sale-btn"
                    title="Trial active until first sale or lead — Tier-1: first lead activates; Tier-2: first sale or lead activates"
                  >
                    <CalendarClock className="h-4 w-4 mr-1" />
                    Until Sale/Lead
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => handleStartProfessionalTrial(null, e)}
                    disabled={bulkActionLoading}
                    className="border-purple-300 text-purple-700 hover:bg-purple-50"
                    data-testid="bulk-start-trial-btn"
                  >
                    <Sparkles className="h-4 w-4 mr-1" />
                    Start Trial
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPlanDialogOpen(true)}
                    disabled={bulkActionLoading}
                    className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 font-semibold"
                    data-testid="bulk-change-plan-btn"
                  >
                    <CreditCard className="h-4 w-4 mr-1" />
                    Change Plan
                  </Button>
                </>
              )}
              {resourceType === 'orders' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBulkInvoiceDownload}
                  disabled={bulkActionLoading}
                  className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                  data-testid="bulk-download-invoices-btn"
                >
                  <FileText className="h-4 w-4 mr-1" />
                  {bulkActionLoading ? 'Generating...' : 'Download Invoices'}
                </Button>
              )}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleBulkDelete}
                disabled={bulkActionLoading}
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            </div>
          </div>
        )}
        
        {/* Data Table */}
        <Card className="hidden md:block">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              {/* iter-221g — Frozen first column (checkbox + first content
                  column, e.g. vendor name) so admins don't lose track of
                  which row they're operating on while scrolling right.
                  Sticky cells need a non-transparent background or text
                  underneath bleeds through, so we paint the same shade
                  the row would have without sticky. */}
              <table className="w-full min-w-[1200px] [&_.frozen-col]:sticky [&_.frozen-col]:left-0 [&_.frozen-col]:z-10 [&_.frozen-col]:bg-white [&_thead_.frozen-col]:bg-gray-50 [&_.frozen-col-2]:left-12 [&_tr.row-selected_.frozen-col]:bg-indigo-50 [&_tr.row-parent-expanded_.frozen-col]:bg-indigo-100 [&_tr.row-parent-collapsed_.frozen-col]:bg-indigo-50 [&_tr.row-child_.frozen-col]:bg-slate-50 [&_tr:hover:not(.row-selected):not(.row-parent-expanded):not(.row-parent-collapsed):not(.row-child)_.frozen-col]:bg-gray-50 [&_.frozen-col-last]:after:absolute [&_.frozen-col-last]:after:inset-y-0 [&_.frozen-col-last]:after:right-0 [&_.frozen-col-last]:after:w-px [&_.frozen-col-last]:after:bg-gray-200 [&_.frozen-col-last]:relative" data-testid={`${resourceType}-table`}>
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="p-4 w-12 frozen-col">
                      <Checkbox
                        checked={selectedItems.size === getDisplayData.length && getDisplayData.length > 0}
                        onCheckedChange={toggleSelectAll}
                        className="h-5 w-5"
                      />
                    </th>
                    {schema.columns.filter(col => !col.afterActions).map((col, idx) => (
                      (!col.adminOnly || (col.adminOnly && user?.role === 'admin')) && (
                        <th key={col.key} className={`text-left p-4 font-semibold text-sm whitespace-nowrap ${idx === 0 ? 'frozen-col frozen-col-2 frozen-col-last min-w-[220px]' : ''}`}>{col.label}</th>
                      )
                    ))}
                    <th className="text-left p-4 font-semibold text-sm whitespace-nowrap">Actions</th>
                    {schema.columns.filter(col => col.afterActions).map(col => (
                      <th key={col.key} className="text-left p-4 font-semibold text-sm whitespace-nowrap">{col.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {getDisplayData.length === 0 ? (
                    <tr><td colSpan={schema.columns.length + 2} className="text-center p-8 text-gray-500">No {schema.title.toLowerCase()} found</td></tr>
                  ) : (
                    getDisplayData.map((item) => {
                      // iter-221g — explicit row-state classes so the sticky
                      // first-column rules above can paint the matching
                      // background colour (sticky cells need a solid bg).
                      const rowStateClass = [
                        selectedItems.has(item.id) ? 'row-selected bg-indigo-50' : '',
                        item._isExpandableParent && item._isExpanded ? 'row-parent-expanded bg-indigo-100/70 font-semibold border-indigo-200' : '',
                        item._isExpandableParent && !item._isExpanded ? 'row-parent-collapsed bg-indigo-50/50 font-medium border-indigo-100 hover:bg-indigo-100/50' : '',
                        item._isChild ? 'row-child bg-slate-50 border-l-4 border-l-indigo-300' : '',
                        !item._isExpandableParent && !item._isChild ? 'hover:bg-gray-50' : '',
                        schema.detailRoute ? 'cursor-pointer' : '',
                      ].filter(Boolean).join(' ');
                      return (
                      <tr key={item.id}
                        className={`border-b transition-colors ${rowStateClass}`}
                        onClick={(e) => {
                          if (!schema.detailRoute) {
                            // No dedicated detail page — open update modal for this resource
                            if (schema.actions?.includes('update')) {
                              setSelectedItem(item);
                              setUpdateData({ status: item.status, priority: item.priority, notes: item.notes || '' });
                              setUpdateDialogOpen(true);
                            } else if (schema.actions?.includes('view')) {
                              setSelectedItem(item);
                              setViewDialogOpen(true);
                            }
                            return;
                          }
                          // iter-221g — Navigation guard. When admin has
                          // any rows checked, an accidental click on a
                          // non-checkbox area used to drop the entire
                          // selection by routing to the detail page.
                          // Confirm before navigating.
                          if (selectedItems.size > 0) {
                            const ok = window.confirm(
                              `You have ${selectedItems.size} ${resourceType === 'vendors' ? 'vendor' : 'item'}${selectedItems.size > 1 ? 's' : ''} selected. ` +
                              `Navigate away and lose the selection?`
                            );
                            if (!ok) return;
                          }
                          navigate(`${schema.detailRoute}/${item.id}`);
                        }}
                        data-testid={`${resourceType}-row-${item.id}${item._isChild ? '-child' : (item._isExpandableParent ? '-parent' : '')}`}>
                        <td className="p-4 w-12 frozen-col">
                          <Checkbox
                            checked={selectedItems.has(item.id)}
                            onCheckedChange={() => toggleSelectItem(item.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="h-5 w-5"
                          />
                        </td>
                        {schema.columns.filter(col => !col.afterActions).map((col, colIdx) => (
                          (!col.adminOnly || (col.adminOnly && user?.role === 'admin')) && (
                            <td key={col.key} className={`p-4 ${colIdx === 0 ? 'frozen-col frozen-col-2 frozen-col-last min-w-[220px]' : ''}`}>
                              {/* First visible column: render expand chevron for parents
                                  and a vertical-rail indent for children. Keeps the
                                  hierarchy obvious without breaking column alignment. */}
                              {colIdx === 0 && item._isExpandableParent && (
                                <button
                                  type="button"
                                  className="inline-flex items-center justify-center mr-1.5 -ml-1 align-middle text-blue-700 hover:bg-blue-100 rounded p-0.5"
                                  data-testid={`expand-parent-${item.id}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedParents(prev => {
                                      const next = new Set(prev);
                                      if (next.has(item.id)) next.delete(item.id); else next.add(item.id);
                                      return next;
                                    });
                                  }}
                                  title={item._isExpanded ? `Collapse ${item._childCount} sub-order${item._childCount === 1 ? '' : 's'}` : `Expand ${item._childCount} sub-order${item._childCount === 1 ? '' : 's'}`}
                                >
                                  {item._isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                </button>
                              )}
                              {colIdx === 0 && item._isChild && (
                                <span className="inline-block w-3 mr-1.5 align-middle border-l-2 border-blue-200" style={{ height: '0.9em' }} aria-hidden="true" />
                              )}
                              {colIdx === 0 && item._isExpandableParent && (
                                <Badge variant="outline" className="ml-1.5 text-[10px] py-0 px-1.5 border-blue-300 text-blue-700 bg-blue-50 align-middle">
                                  {item._childCount} sub-order{item._childCount === 1 ? '' : 's'}
                                </Badge>
                              )}
                              {colIdx === 0 && item._isChild && (
                                <Badge variant="outline" className="ml-1.5 text-[10px] py-0 px-1.5 border-gray-300 text-gray-600 bg-white align-middle">
                                  sub-order
                                </Badge>
                              )}
                              {renderCellContent(item, col)}
                            </td>
                          )
                        ))}
                        <td className="p-4">{renderRowActions(item)}</td>
                        {schema.columns.filter(col => col.afterActions).map(col => (
                          <td key={col.key} className="p-4">{renderCellContent(item, col)}</td>
                        ))}
                      </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        
        {/* Mobile Cards - Use Lite components for orders */}
        <div className="md:hidden">
          {/* Orders Lite View */}
          {resourceType === 'orders' && isMobile ? (
            <Card>
              <CardContent className="p-0">
                <OrdersListLite 
                  orders={getDisplayData}
                  loading={loading}
                />
              </CardContent>
            </Card>
          ) : (
            /* Default Mobile Cards for other resource types */
            <div className="space-y-3">
              {getDisplayData.length === 0 ? (
                <Card><CardContent className="p-6 text-center text-gray-500">No {schema.title.toLowerCase()} found</CardContent></Card>
              ) : (
                getDisplayData.map((item) => (
                  <Card key={item.id} className={`cursor-pointer hover:shadow-md transition-shadow ${selectedItems.has(item.id) ? 'ring-2 ring-indigo-500' : ''}`}
                    onClick={() => {
                      if (!schema.detailRoute) return;
                      if (selectedItems.size > 0) {
                        const ok = window.confirm(
                          `You have ${selectedItems.size} ${resourceType === 'vendors' ? 'vendor' : 'item'}${selectedItems.size > 1 ? 's' : ''} selected. ` +
                          `Navigate away and lose the selection?`
                        );
                        if (!ok) return;
                      }
                      navigate(`${schema.detailRoute}/${item.id}`);
                    }}
                    data-testid={`${resourceType}-card-${item.id}`}>
                    <CardContent className="p-4">
                      <div className="flex gap-3">
                        <div className="flex-1">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <p className="font-semibold">{item.name || item.order_number || item.customer_name || item.id?.substring(0, 8)}</p>
                              <p className="text-xs text-gray-500">{item.email || item.customer?.email || item.customer_email}</p>
                            </div>
                            {renderStatusBadge(item.status)}
                          </div>
                          <div className="flex justify-between items-center pt-2 border-t">
                            <span className="text-sm font-semibold text-green-600">
                              {formatCurrency(item.lifetime_value || item.total_amount || item.vendor_profile?.metrics?.total_sales || 0)}
                            </span>
                            {renderRowActions(item)}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Add Customer Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Customer</DialogTitle>
            <DialogDescription>Create a new customer account</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Customer name" data-testid="add-customer-name" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={formData.email || ''} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="customer@example.com" data-testid="add-customer-email" />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input type="password" value={formData.password || ''} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="Password" data-testid="add-customer-password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddCustomer} data-testid="add-customer-submit">Add Customer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Customer Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value })} data-testid="edit-customer-name" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={formData.email || ''} disabled />
              <p className="text-xs text-gray-500">Email cannot be changed</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleEditCustomer} data-testid="edit-customer-submit">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Customer</DialogTitle>
            <DialogDescription>Are you sure you want to delete {selectedItem?.name}? This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteCustomer} data-testid="delete-customer-confirm">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Commission Dialog */}
      <Dialog open={commissionDialogOpen} onOpenChange={setCommissionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Commission Rate</DialogTitle>
            <DialogDescription>Set commission rate for {selectedItem?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Commission Rate (%)</Label>
              <Input type="number" min="0" max="100" step="0.1" value={newCommission} onChange={(e) => setNewCommission(e.target.value)} data-testid="commission-rate-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCommissionDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateCommission} data-testid="commission-submit">Update Commission</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Message to Vendor Dialog (Admin → Vendor) */}
      <Dialog open={messageDialogOpen} onOpenChange={setMessageDialogOpen}>
        <DialogContent data-testid="vendor-message-dialog">
          <DialogHeader>
            <DialogTitle>Send Message to {messageTarget?.name || 'Vendor'}</DialogTitle>
            <DialogDescription>
              The vendor will see this in their bell icon and (if subscribed) receive a desktop push notification.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                placeholder="e.g., Action Required: Tax Document"
                value={messageForm.title}
                onChange={(e) => setMessageForm(p => ({ ...p, title: e.target.value }))}
                data-testid="vendor-message-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Message *</Label>
              <textarea
                rows={4}
                className="w-full rounded-md border border-gray-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Write your message to the vendor..."
                value={messageForm.message}
                onChange={(e) => setMessageForm(p => ({ ...p, message: e.target.value }))}
                data-testid="vendor-message-body-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <select
                className="w-full rounded-md border border-gray-300 p-2 text-sm"
                value={messageForm.priority}
                onChange={(e) => setMessageForm(p => ({ ...p, priority: e.target.value }))}
                data-testid="vendor-message-priority-select"
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High (default)</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div className="flex items-center gap-3 pt-1">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={messageForm.send_push}
                  onCheckedChange={(v) => setMessageForm(p => ({ ...p, send_push: !!v }))}
                  data-testid="vendor-message-push-toggle"
                />
                Web Push
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={messageForm.send_email}
                  onCheckedChange={(v) => setMessageForm(p => ({ ...p, send_email: !!v }))}
                  data-testid="vendor-message-email-toggle"
                />
                Email
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMessageDialogOpen(false)} disabled={sendingMessage}>Cancel</Button>
            <Button
              onClick={async () => {
                if (!messageForm.title.trim() || !messageForm.message.trim()) {
                  toast({ title: 'Missing fields', description: 'Title and message are required', variant: 'destructive' });
                  return;
                }
                if (!messageTarget?.id) return;
                setSendingMessage(true);
                try {
                  const res = await apiCall(`/api/admin/vendors/${messageTarget.id}/message`, {
                    method: 'POST',
                    body: JSON.stringify(messageForm)
                  });
                  if (res?.success) {
                    toast({ title: 'Message sent', description: `Delivered to ${res.vendor_name || 'vendor'} via ${(res.channels_sent || []).join(', ') || 'in-app'}` });
                    setMessageDialogOpen(false);
                  } else {
                    toast({ title: 'Could not send', description: 'Please try again', variant: 'destructive' });
                  }
                } catch (err) {
                  toast({ title: 'Error', description: err?.message || 'Failed to send message', variant: 'destructive' });
                } finally {
                  setSendingMessage(false);
                }
              }}
              disabled={sendingMessage}
              data-testid="vendor-message-send-btn"
            >
              {sendingMessage ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MessageSquare className="h-4 w-4 mr-2" />}
              Send Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Lead Update Dialog */}
      <Dialog open={updateDialogOpen} onOpenChange={setUpdateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Lead</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={updateData.status} onValueChange={(v) => setUpdateData({ ...updateData, status: v })}>
                <SelectTrigger data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="qualified">Qualified</SelectItem>
                  <SelectItem value="converted">Converted</SelectItem>
                  <SelectItem value="lost">Lost</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={updateData.priority} onValueChange={(v) => setUpdateData({ ...updateData, priority: v })}>
                <SelectTrigger data-testid="lead-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input value={updateData.notes} onChange={(e) => setUpdateData({ ...updateData, notes: e.target.value })} placeholder="Add notes..." data-testid="lead-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpdateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateLead} data-testid="lead-update-submit">Update Lead</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* View Lead Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Lead Details</DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Customer</p>
                  <p className="font-medium">{selectedItem.customer_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium">{selectedItem.customer_email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium">{selectedItem.customer_phone || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Listing</p>
                  <p className="font-medium">{selectedItem.listing_title}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  {renderLeadStatusBadge(selectedItem.status)}
                </div>
                <div>
                  <p className="text-sm text-gray-500">Priority</p>
                  {renderPriorityBadge(selectedItem.priority)}
                </div>
              </div>
              {selectedItem.message && (
                <div>
                  <p className="text-sm text-gray-500">Message</p>
                  <p className="mt-1 p-3 bg-gray-50 rounded">{selectedItem.message}</p>
                </div>
              )}
              {selectedItem.notes && (
                <div>
                  <p className="text-sm text-gray-500">Notes</p>
                  <p className="mt-1 p-3 bg-gray-50 rounded">{selectedItem.notes}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Close</Button>
            <Button onClick={() => { setViewDialogOpen(false); setUpdateData({ status: selectedItem.status, priority: selectedItem.priority, notes: selectedItem.notes || '' }); setUpdateDialogOpen(true); }}>
              Update Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Trial Extension Dialog */}
      <Dialog open={trialDialogOpen} onOpenChange={setTrialDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-teal-600" />
              {trialTargetVendor ? 'Extend Trial' : `Extend Trial (${selectedItems.size} vendors)`}
            </DialogTitle>
            <DialogDescription>
              {trialTargetVendor
                ? `Set a new trial end date for ${trialTargetVendor.name}`
                : `Set a new trial end date for ${selectedItems.size} selected vendor(s)`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center py-4" data-testid="trial-extension-calendar">
            <Calendar
              mode="single"
              selected={trialDate}
              onSelect={setTrialDate}
              disabled={(date) => date < new Date()}
              initialFocus
              className="rounded-md border"
            />
            {trialDate && (
              <p className="text-sm text-gray-600 mt-3">
                New trial end: <span className="font-semibold">{trialDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTrialDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleTrialExtension}
              disabled={!trialDate || trialSaving}
              data-testid="trial-extension-save-btn"
            >
              {trialSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CalendarClock className="h-4 w-4 mr-2" />}
              {trialSaving ? 'Saving...' : 'Set Trial Date'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Subscription Plan Dialog */}
      <Dialog open={planDialogOpen} onOpenChange={(o) => { setPlanDialogOpen(o); if (!o) { setSelectedPlanCode(''); setPlanChangeReason(''); } }}>
        <DialogContent className="sm:max-w-lg" data-testid="change-plan-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-indigo-600" />
              Change Subscription Plan — {selectedItems.size} vendor{selectedItems.size !== 1 ? 's' : ''}
            </DialogTitle>
            <DialogDescription>
              The commission rate is derived from the plan. It cannot be overridden manually.
              A subscription history entry will be created for each vendor.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Select Plan</label>
              <div className="grid grid-cols-1 gap-2">
                {PLAN_OPTIONS.map(plan => (
                  <button
                    key={plan.code}
                    type="button"
                    onClick={() => setSelectedPlanCode(plan.code)}
                    data-testid={`plan-option-${plan.code}`}
                    className={`flex items-center justify-between px-4 py-3 rounded-lg border-2 text-left transition-all ${
                      selectedPlanCode === plan.code
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-900'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full border-2 flex items-center justify-center ${selectedPlanCode === plan.code ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300'}`}>
                        {selectedPlanCode === plan.code && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                      </div>
                      <span className="font-semibold">{plan.label}</span>
                    </div>
                    <span className={`text-sm font-mono font-bold px-2 py-0.5 rounded ${
                      selectedPlanCode === plan.code ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {plan.commission}%
                    </span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Reason <span className="text-gray-400 font-normal">(optional)</span></label>
              <input
                type="text"
                placeholder="e.g. Correcting incorrect plan assignment"
                value={planChangeReason}
                onChange={e => setPlanChangeReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                data-testid="plan-change-reason-input"
              />
            </div>
            {selectedPlanCode && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                <strong>This will:</strong>
                <ul className="list-disc pl-4 mt-1 space-y-0.5">
                  <li>Set plan to <strong>{PLAN_OPTIONS.find(p => p.code === selectedPlanCode)?.label}</strong> with <strong>{PLAN_OPTIONS.find(p => p.code === selectedPlanCode)?.commission}% commission</strong></li>
                  <li>Close the current open subscription history record for each vendor</li>
                  <li>Create a new history entry with your admin email as "Changed By"</li>
                  <li>Update effective_commission_rate on vendor_subscriptions</li>
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleBulkChangePlan}
              disabled={!selectedPlanCode || planChangeSaving}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="confirm-change-plan-btn"
            >
              {planChangeSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />}
              {planChangeSaving ? 'Applying...' : `Change Plan for ${selectedItems.size} Vendor${selectedItems.size !== 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Filter Drawer */}
      {schema.hasFilterDrawer && (
        <FilterDrawer
          isOpen={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          filters={[]}
          onApplyFilters={setAppliedFilters}
          activeFiltersCount={0}
        />
      )}
    </DashboardLayout>
  );
};

export default UniversalResourceTable;
